import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import type {
  CategoryFilterInput,
  CategoryUpdateInput,
  CategoryCreationInput,
  CreatePageCategoryMutation,
  CreatePageCategoryMutationVariables,
  UpdatePageCategoryMutation,
  UpdatePageCategoryMutationVariables,
  UpdatePageCategoryOrderMutation,
  UpdatePageCategoryOrderMutationVariables,
} from '../../../../__generated__/queries-web';
import type { Category } from '../../types';
import { PAGE_CATEGORY_FRAGMENT } from '../fragments/category.web.graphql';
import { GET_PAGE_CATEGORIES } from '../queries/categories.web.graphql';
import { getTabsMutationVariables, getTagsMutationVariables } from '../utils';

/// UPDATE CATEGORY ////////////////////////////////////////////
const UPDATE_PAGE_CATEGORY = gql`
  mutation UpdatePageCategory(
    $where: CategoryUniqueFilterInput!
    $data: CategoryUpdateInput!
  ) {
    updateCategory(where: $where, data: $data) {
      ...Category
    }
  }
  ${PAGE_CATEGORY_FRAGMENT}
`;

export const useUpdatePageCategoryLockerId = () => {
  const [updatePageCategory] = useMutation<
    UpdatePageCategoryMutation,
    UpdatePageCategoryMutationVariables
  >(UPDATE_PAGE_CATEGORY);

  return useCallback(
    (
      id: string | undefined,
      lockerId: string | null,
      noCacheUpdate?: boolean,
    ) => {
      if (!id) return Promise.resolve();
      return updatePageCategory({
        variables: {
          where: { id },
          data: { lockerId },
        },
        fetchPolicy: noCacheUpdate ? 'no-cache' : undefined,
      });
    },
    [updatePageCategory],
  );
};

/// CREATE CATEGORY ////////////////////////////////////////////
const CREATE_PAGE_CATEGORY = gql`
  mutation CreatePageCategory($data: CategoryCreationInput!) {
    createCategory(data: $data) {
      ...Category
    }
  }
  ${PAGE_CATEGORY_FRAGMENT}
`;

export interface UseSavePageCategoryVariables {
  data: Partial<Category> & { hasDragAndDropOccurred?: boolean };
  where: CategoryFilterInput;
}

export const useSavePageCategory = () => {
  const [updateCategory, { client }] = useMutation<
    UpdatePageCategoryMutation,
    UpdatePageCategoryMutationVariables
  >(UPDATE_PAGE_CATEGORY);
  const [createCategory] = useMutation<
    CreatePageCategoryMutation,
    CreatePageCategoryMutationVariables
  >(CREATE_PAGE_CATEGORY);

  return ({ data, where = {} }: UseSavePageCategoryVariables) => {
    const {
      parent,
      subtitle,
      description,
      medias,
      body,
      slug,
      hasDragAndDropOccurred,
      ...restData
    } = data;

    // Get tabs from the cache to check if we need to delete all tabs
    const tabsCache =
      client.readFragment({
        id: `Category:${where.id || ''}`,
        fragment: PAGE_CATEGORY_FRAGMENT,
      })?.tabs || [];

    // Check if we had tabs before and now we don't have any
    const hadTabsBefore = tabsCache.length > 0;
    const hasTabsNow = data?.tabs && data.tabs.length > 0;
    const shouldDeleteAllTabs = hadTabsBefore && !hasTabsNow;

    const newVariables = {
      data: {
        ...restData,
        slug: slug || null,
        subtitle: subtitle || null,
        description: description || null,
        medias: typeof medias === 'string' ? JSON.parse(medias) : medias,
        body: body ?? null,
        ...((data?.tabs && data.tabs.length > 0) || shouldDeleteAllTabs
          ? {
              tabs: getTabsMutationVariables(
                {
                  data: { ...restData, hasDragAndDropOccurred },
                  where,
                },
                client,
              ),
            }
          : {
              tabs: { create: [] },
            }),
        tags: getTagsMutationVariables(
          {
            data: { ...data, hasDragAndDropOccurred },
            where,
          },
          Boolean(where.id),
          client,
        ),
      },
      where,
    };

    return where.id
      ? updateCategory({
          variables: {
            ...newVariables,
            data: {
              ...newVariables.data,
            } as CategoryUpdateInput,
          },
        })
      : createCategory({
          variables: {
            ...newVariables,
            data: {
              ...newVariables.data,
              parent: data.parent?.id
                ? { connect: { id: data.parent.id } }
                : null,
            } as CategoryCreationInput,
          },
        });
  };
};

/// UPDATE CATEGORY ORDER ////////////////////////////////////////////
const UPDATE_PAGE_CATEGORY_ORDER = gql`
  mutation UpdatePageCategoryOrder(
    $where: CategoryUniqueFilterInput!
    $data: CategoryUpdateInput!
  ) {
    updateCategory(where: $where, data: $data) {
      id
      order
    }
  }
`;

export const useUpdatePageCategoryOrder = () => {
  const [updatePageCategoryOrder, { loading }] = useMutation<
    UpdatePageCategoryOrderMutation,
    UpdatePageCategoryOrderMutationVariables
  >(UPDATE_PAGE_CATEGORY_ORDER, {
    awaitRefetchQueries: true,
    refetchQueries: [GET_PAGE_CATEGORIES],
  });

  const updateOrder = useCallback(
    (id: string, order: number) =>
      updatePageCategoryOrder({
        variables: {
          data: { order },
          where: { id },
        },
      }),
    [updatePageCategoryOrder],
  );

  return { isUpdatingOrder: loading, updateOrder };
};
